/**
 * Personnalisation produit — transversal, piloté par tag Shopify.
 *
 * Deux modes, déterminés côté Liquid (snippets/product-personalization.liquid)
 * à partir des tags du produit :
 * - "included" (tag "personnalisation-fond-plaque") : champs toujours visibles,
 *   "Numéro / Nombre" obligatoire, aucun supplément (déjà inclus dans le prix).
 *   Le formulaire natif (product-form.js) gère l'ajout au panier normalement,
 *   ce script se contente de bloquer la soumission si le numéro est vide.
 * - "optional" (tous les autres produits) : case à cocher désactivée par défaut.
 *   Quand elle est cochée, ce script prend la main sur la soumission du
 *   formulaire pour ajouter, en un seul appel atomique à /cart/add.js, la
 *   ligne du produit ET la ligne du supplément de personnalisation (produit
 *   technique masqué). Dans tous les autres cas (case décochée, ou aucune
 *   personnalisation sur la page), ce script n'intervient jamais et le
 *   comportement natif du thème (product-form.js) reste inchangé.
 *
 * Interception : un écouteur "submit" est posé en phase de capture sur le
 * <form> du produit, ce qui garantit qu'il s'exécute avant le handler natif
 * de <product-form> (attaché en phase de bulle). Cela permet de valider ou de
 * prendre la main sur la soumission sans jamais modifier product-form.js.
 */
if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    class ProductPersonalization extends HTMLElement {
      constructor() {
        super();

        this.mode = this.dataset.mode;
        this.toggle = this.querySelector('[data-personalization-toggle]');
        this.fieldsWrapper = this.querySelector('[data-personalization-fields]');
        this.numberInput = this.querySelector('[data-personalization-field="number"]');
        this.nameInput = this.querySelector('[data-personalization-field="name"]');
        this.fieldError = this.querySelector('[data-personalization-error]');
        this.form = this.closest('form');
        this.productFormElement = this.closest('product-form');
        this.cart = document.querySelector('cart-drawer');

        if (this.toggle) {
          this.toggle.addEventListener('change', this.onToggleChange.bind(this));
        }

        if (this.numberInput) {
          this.numberInput.addEventListener('input', this.clearFieldError.bind(this));
        }

        if (this.form) {
          this.form.addEventListener('submit', this.onFormSubmit.bind(this), true);
          // Kit de pose (app Essential Upsell, bloc "Achetés ensemble") : son bouton
          // "Ajouter au panier" est un type="button" qui ne déclenche pas le submit
          // du formulaire. On applique donc la même règle "Numéro obligatoire" à ce
          // bouton, en phase de capture, avant le gestionnaire de l'application.
          this.form.addEventListener('click', this.onUpsellClick.bind(this), true);
          // Le bloc de l'application a son propre sélecteur de variante : on le
          // garde aligné sur la couleur choisie dans le formulaire du thème.
          this.form.addEventListener('change', () => {
            setTimeout(() => this.syncUpsellVariant(), 400);
          });
        }
      }

      // Aligne la variante du produit principal dans le bloc "Kit de pose"
      // (Essential Upsell) sur la variante sélectionnée dans le thème.
      // Renvoie true si une correction a été appliquée.
      syncUpsellVariant() {
        if (!this.form) return false;
        const select = this.form.querySelector('[data-essential-upsell-element="container"] select');
        const variantId = String(this.currentVariantId() || '');
        if (!select || !variantId || select.value === variantId) return false;
        if (![...select.options].some((option) => option.value === variantId)) return false;
        const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;
        setter.call(select, variantId);
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      // Modes où le premier champ ("number" : Numéro pour les fonds de plaque,
      // Modèle de votre moto pour les jantes) est obligatoire, sans supplément.
      isRequiredMode() {
        return this.mode === 'included' || this.mode === 'jantes';
      }

      onUpsellClick(evt) {
        const button = evt.target.closest('[data-essential-upsell-element="add-to-cart-button"]');
        if (!button || !this.form.contains(button)) return;

        if (this.isRequiredMode()) {
          const value = this.numberInput ? this.numberInput.value.trim() : '';
          if (!value) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.showFieldError();
            return;
          }
          this.clearFieldError();
        }

        // Variante du bloc différente de celle du thème : on corrige puis on
        // relance le clic, pour que l'application ajoute la bonne couleur.
        if (this.syncUpsellVariant()) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          setTimeout(() => button.click(), 250);
        }
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber =
          typeof subscribe === 'function'
            ? subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this))
            : undefined;
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      currentVariantId() {
        const idInput = this.form ? this.form.querySelector('[name="id"]') : null;
        return idInput ? idInput.value : this.dataset.productVariantId;
      }

      // Une fois le produit ajouté (par ce composant ou par le flux natif), on
      // remet la case à cocher facultative à zéro, comme le fait le thème pour
      // le formulaire "destinataire carte cadeau" (assets/recipient-form.js).
      onCartUpdate(event) {
        if (this.mode !== 'optional') return;
        if (event.source !== 'product-form' && event.source !== 'product-personalization') return;
        if (String(event.productVariantId) !== String(this.currentVariantId())) return;
        if (this.toggle) this.toggle.checked = false;
        this.onToggleChange();
      }

      onToggleChange() {
        const checked = !!(this.toggle && this.toggle.checked);

        [this.numberInput, this.nameInput].forEach((field) => {
          if (field) field.disabled = !checked;
        });

        if (this.fieldsWrapper) {
          this.fieldsWrapper.classList.toggle('hidden', !checked);
        }

        if (!checked) {
          this.clearFields();
          this.clearFieldError();
        }
      }

      clearFields() {
        if (this.numberInput) this.numberInput.value = '';
        if (this.nameInput) this.nameInput.value = '';
      }

      clearFieldError() {
        if (this.fieldError) this.fieldError.classList.add('hidden');
        if (this.numberInput) this.numberInput.removeAttribute('aria-invalid');
      }

      showFieldError() {
        if (this.fieldError) this.fieldError.classList.remove('hidden');
        if (this.numberInput) {
          this.numberInput.setAttribute('aria-invalid', 'true');
          this.numberInput.focus();
        }
      }

      // Réutilise le bloc d'erreur natif de <product-form> (celui que
      // product-form.js remplit déjà pour les erreurs de panier), pour rester
      // visuellement identique au reste du thème.
      handleCartError(message) {
        if (!this.productFormElement) return;
        if (this.productFormElement.dataset.hideErrors === 'true') return;
        const wrapper = this.productFormElement.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) return;
        const messageEl = wrapper.querySelector('.product-form__error-message');
        wrapper.toggleAttribute('hidden', !message);
        if (message && messageEl) messageEl.textContent = message;
      }

      setLoading(isLoading) {
        const submitButton = this.productFormElement
          ? this.productFormElement.querySelector('[type="submit"]')
          : null;
        const spinner = this.productFormElement
          ? this.productFormElement.querySelector('.loading-overlay__spinner')
          : null;
        if (!submitButton) return;

        if (isLoading) {
          submitButton.setAttribute('aria-disabled', true);
          submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      onFormSubmit(evt) {
        // Fond de plaque personnalisé (et tout futur tag "inclus") : le numéro
        // est obligatoire ; le reste de l'ajout au panier suit le flux natif.
        // Même règle pour les kits déco jantes (mode "jantes" : modèle obligatoire).
        if (this.isRequiredMode()) {
          const value = this.numberInput ? this.numberInput.value.trim() : '';
          if (!value) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.showFieldError();
            return;
          }
          this.clearFieldError();
          return;
        }

        // Produit standard, case non cochée : comportement natif inchangé.
        if (!this.toggle || !this.toggle.checked) {
          return;
        }

        // Produit standard, personnalisation cochée : on prend la main pour
        // ajouter le produit ET le supplément en un seul appel atomique.
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.addWithSurcharge();
      }

      addWithSurcharge() {
        const surchargeVariantId = this.dataset.surchargeVariantId;
        if (!surchargeVariantId || !this.form) return;

        this.handleCartError(false);
        this.setLoading(true);

        const bundleId = `perso-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        const formData = new FormData(this.form);
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const variantId = formData.get('id');

        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        });
        // Propriété technique (préfixe "_") : masquée du client par convention
        // du thème (voir sections/main-cart-items.liquid), sert uniquement à
        // relier la ligne de supplément à sa ligne de produit.
        properties['_perso_bundle'] = bundleId;

        const items = [
          {
            id: variantId,
            quantity: quantity,
            properties: properties,
          },
          {
            id: surchargeVariantId,
            quantity: quantity,
            properties: {
              _perso_bundle: bundleId,
              'Produit personnalisé': this.dataset.productTitle || '',
            },
          },
        ];

        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const body = { items: items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const message =
                response.description ||
                response.message ||
                (window.cartStrings && window.cartStrings.error) ||
                '';
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-personalization',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: message,
              });
              this.handleCartError(message);
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-personalization',
              productVariantId: variantId,
            });
            this.cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.setLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          });
      }
    }
  );
}
